import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Smartphone as PWAIcon,
  Storage as StorageIcon,
  CloudSync as SyncIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import offlineStorage, { getStorageEstimate } from '../utils/offlineStorage';
import { getPendingRequestCount, processPendingRequests } from '../services/api';

/**
 * PWA Settings Component
 * Manages offline data, storage, and PWA features
 */
const PWASettings = () => {
  const [storageInfo, setStorageInfo] = useState(null);
  const [cachedData, setCachedData] = useState({
    sales: 0,
    inventory: 0,
    leads: 0,
    pendingRequests: 0
  });
  const [lastSync, setLastSync] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStorageInfo();
    checkInstallStatus();
  }, []);

  const checkInstallStatus = () => {
    const installed = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsInstalled(installed);
  };

  const loadStorageInfo = async () => {
    try {
      // Get storage estimate
      const estimate = await getStorageEstimate();
      setStorageInfo(estimate);

      // Get cached data counts
      const [sales, inventory, leads, pendingCount, syncTime] = await Promise.all([
        offlineStorage.sales.getAll(),
        offlineStorage.inventory.getAll(),
        offlineStorage.leads.getAll(),
        getPendingRequestCount(),
        offlineStorage.getLastSync()
      ]);

      setCachedData({
        sales: sales.length,
        inventory: inventory.length,
        leads: leads.length,
        pendingRequests: pendingCount
      });

      setLastSync(syncTime);
    } catch (error) {
      console.error('Failed to load storage info:', error);
      setError('Failed to load storage information');
    }
  };

  const handleClearCache = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await offlineStorage.clearAllData();

      // Clear service worker caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      setSuccess('All cached data has been cleared');
      setClearDialogOpen(false);
      await loadStorageInfo();
    } catch (error) {
      console.error('Failed to clear cache:', error);
      setError('Failed to clear cached data');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNow = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const results = await processPendingRequests();

      if (results) {
        if (results.success.length > 0) {
          setSuccess(`Successfully synced ${results.success.length} pending requests`);
        }
        if (results.failed.length > 0) {
          setError(`Failed to sync ${results.failed.length} requests`);
        }
      } else {
        setError('No pending requests to sync');
      }

      await loadStorageInfo();
    } catch (error) {
      console.error('Failed to sync:', error);
      setError('Failed to sync pending requests');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const getStoragePercent = () => {
    if (!storageInfo || !storageInfo.quota) return 0;
    return (storageInfo.usage / storageInfo.quota) * 100;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Progressive Web App Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Manage offline data, storage, and PWA features
          </Typography>
        </Grid>

        {/* PWA Installation Status */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PWAIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Installation Status
                </Typography>
              </Box>

              {isInstalled ? (
                <Alert severity="success" icon={<CheckIcon />}>
                  PaceEstate is installed as a Progressive Web App
                </Alert>
              ) : (
                <Alert severity="info" icon={<InfoIcon />}>
                  Install PaceEstate for offline access and quick launch.
                  Look for the install prompt or use your browser's "Add to Home Screen" option.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Storage Usage */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Storage Usage
                </Typography>
              </Box>

              {storageInfo ? (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Used
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatBytes(storageInfo.usage)} / {formatBytes(storageInfo.quota)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getStoragePercent()}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: getStoragePercent() > 80 ? 'error.main' : 'primary.main'
                        }
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {getStoragePercent().toFixed(1)}% of available storage used
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Storage information not available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cached Data */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Offline Data
                </Typography>
              </Box>

              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Sales"
                    secondary={`${cachedData.sales} sales cached for offline access`}
                  />
                  <Chip label={cachedData.sales} size="small" />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Inventory Items"
                    secondary={`${cachedData.inventory} items cached for offline access`}
                  />
                  <Chip label={cachedData.inventory} size="small" />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Leads"
                    secondary={`${cachedData.leads} leads cached for offline access`}
                  />
                  <Chip label={cachedData.leads} size="small" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {cachedData.pendingRequests > 0 ? (
                      <WarningIcon color="warning" />
                    ) : (
                      <CheckIcon color="success" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Pending Requests"
                    secondary={
                      cachedData.pendingRequests > 0
                        ? `${cachedData.pendingRequests} requests waiting to sync`
                        : 'No pending requests'
                    }
                  />
                  <Chip
                    label={cachedData.pendingRequests}
                    size="small"
                    color={cachedData.pendingRequests > 0 ? 'warning' : 'success'}
                  />
                </ListItem>
              </List>

              {lastSync && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Last synced: {new Date(lastSync).toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Actions
              </Typography>

              {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={handleSyncNow}
                  disabled={loading || cachedData.pendingRequests === 0}
                >
                  Sync Now
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setClearDialogOpen(true)}
                  disabled={loading}
                >
                  Clear Cached Data
                </Button>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                Clearing cached data will remove all offline data. You'll need to reload the app
                to re-download data for offline access.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Clear Cache Confirmation Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
      >
        <DialogTitle>Clear Cached Data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all offline data including cached sales, inventory, and leads.
            You'll need to reload the application to re-download data for offline access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleClearCache} color="error" variant="contained">
            Clear Data
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PWASettings;
